import React, { useState } from "react";
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input
} from "@heroui/react";
import { Creator, staffApi } from "../../../../api/staff";
import { useTranslations } from "../../../../contexts/translation.context";
import { showPartnerSuccess, showError } from "../../../../utils/toast";
import { ConfirmModal } from "../../../../components";

interface CreatorActionsCellProps {
  creator: Creator;
  onRefresh?: () => void;
}

const CreatorActionsCell: React.FC<CreatorActionsCellProps> = ({ creator, onRefresh }) => {
  const { t } = useTranslations();
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    action: 'approve' | 'reject' | 'deactivate' | null;
  }>({
    isOpen: false,
    action: null,
  });
  const [balanceModal, setBalanceModal] = useState(false);
  const [balanceAmount, setBalanceAmount] = useState("");

  const handleAction = async (key: React.Key) => {
    const action = key as string;

    if (action === 'topUpBalance') {
      setBalanceAmount("");
      setBalanceModal(true);
      return;
    }

    if (action === 'deactivate') {
      setConfirmModal({ isOpen: true, action });
    } else {
      await performAction(action as 'approve' | 'reject' | 'deactivate');
    }
  };

  const handleTopUpBalance = async () => {
    const amount = parseFloat(balanceAmount);
    if (isNaN(amount) || amount === 0) {
      showError(t('admin.creators.actions.topUpBalance.invalidAmount'));
      return;
    }

    setIsProcessing(true);
    try {
      await staffApi.changeCreatorBalance(creator.id, amount);
      showPartnerSuccess(t('admin.creators.actions.topUpBalance.success'));
      setBalanceModal(false);
      setBalanceAmount("");
      onRefresh?.();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail
        || error?.response?.data?.message
        || error?.message
        || t('admin.creators.actions.error');
      showError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const performAction = async (action: 'approve' | 'reject' | 'deactivate') => {
    console.log('Performing action:', action, 'for creator:', creator);
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      switch (action) {
        case 'approve':
          console.log('Approving creator, setting status to: active');
          await staffApi.changeCreatorStatus(creator.id, 'active');
          // Если деактивированный пользователь активируется
          if (creator.status === 'deactivated') {
            showPartnerSuccess(t('admin.creators.actions.activateSuccess'));
          } else {
            showPartnerSuccess(t('admin.creators.actions.approveSuccess'));
          }
          break;
        case 'reject':
          console.log('Rejecting creator, setting status to: wait_moderation');
          await staffApi.changeCreatorStatus(creator.id, 'wait_moderation');
          showPartnerSuccess(t('admin.creators.actions.rejectSuccess'));
          break;
        case 'deactivate':
          console.log('Deactivating creator, setting status to: deactivated');
          await staffApi.changeCreatorStatus(creator.id, 'deactivated');
          showPartnerSuccess(t('admin.creators.actions.deactivateSuccess'));
          break;
      }
      onRefresh?.();
    } catch (error: any) {
      console.error('Action failed:', action);
      console.error('Error details:', {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
        detail: error?.response?.data?.detail
      });

      // Извлекаем детальное сообщение об ошибке из ответа API
      const errorMessage = error?.response?.data?.detail
        || error?.response?.data?.detail?.[0]?.msg
        || error?.response?.data?.message
        || error?.message
        || t('admin.creators.actions.error');
      showError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirm = () => {
    if (confirmModal.action) {
      performAction(confirmModal.action);
    }
  };

  // Determine which actions to show based on status
  const isWaitModeration = creator.status === 'wait_moderation';
  const isDeactivated = creator.status === 'deactivated';

  return (
    <>
      <div className="flex justify-end">
        <Dropdown>
          <DropdownTrigger>
            <Button
              size="sm"
              variant="light"
              aria-label="More options"
              className="min-w-[32px] w-[32px] h-[32px] p-0"
              isDisabled={isProcessing}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 5.83333C10.4603 5.83333 10.8333 5.46033 10.8333 5C10.8333 4.53967 10.4603 4.16667 10 4.16667C9.53968 4.16667 9.16668 4.53967 9.16668 5C9.16668 5.46033 9.53968 5.83333 10 5.83333Z" fill="#71717A"/>
                <path d="M10 10.8333C10.4603 10.8333 10.8333 10.4603 10.8333 10C10.8333 9.53967 10.4603 9.16667 10 9.16667C9.53968 9.16667 9.16668 9.53967 9.16668 10C9.16668 10.4603 9.53968 10.8333 10 10.8333Z" fill="#71717A"/>
                <path d="M10 15.8333C10.4603 15.8333 10.8333 15.4603 10.8333 15C10.8333 14.5397 10.4603 14.1667 10 14.1667C9.53968 14.1667 9.16668 14.5397 9.16668 15C9.16668 15.4603 9.53968 15.8333 10 15.8333Z" fill="#71717A"/>
              </svg>
            </Button>
          </DropdownTrigger>
          <DropdownMenu
            aria-label="Creator actions"
            onAction={handleAction}
          >
            <DropdownItem key="topUpBalance">
              {t('admin.creators.actions.topUpBalance.title')}
            </DropdownItem>
            {isWaitModeration && (
              <>
                <DropdownItem key="approve">
                  {t('admin.creators.actions.approve')}
                </DropdownItem>
                <DropdownItem key="reject">
                  {t('admin.creators.actions.reject')}
                </DropdownItem>
              </>
            )}
            {isDeactivated ? (
              <DropdownItem key="approve" className="text-success" color="success">
                {t('admin.creators.actions.activate')}
              </DropdownItem>
            ) : (
              !isWaitModeration && (
                <DropdownItem key="deactivate" className="text-danger" color="danger">
                  {t('admin.creators.actions.deactivate')}
                </DropdownItem>
              )
            )}
          </DropdownMenu>
        </Dropdown>
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, action: null })}
        onConfirm={handleConfirm}
        title={t('admin.creators.actions.deactivate')}
        message={`${t('admin.creators.actions.deactivateConfirm')} "${creator.name}"?`}
        confirmText={t('admin.creators.actions.deactivate')}
        cancelText={t('global.cancel')}
        confirmColor="danger"
        isLoading={isProcessing}
      />

      <Modal isOpen={balanceModal} onClose={() => setBalanceModal(false)} size="md">
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            {t('admin.creators.actions.topUpBalance.title')}
          </ModalHeader>
          <ModalBody>
            <p className="text-[14px] text-[#71717A] mb-4">
              {t('admin.creators.actions.topUpBalance.description')} <strong>{creator.name}</strong>
            </p>
            <Input
              type="number"
              label={t('admin.creators.actions.topUpBalance.amountLabel')}
              placeholder={t('admin.creators.actions.topUpBalance.amountPlaceholder')}
              value={balanceAmount}
              onValueChange={setBalanceAmount}
              startContent={
                <span className="text-[#71717A] text-[14px]">$</span>
              }
              classNames={{
                input: "text-[14px]",
                label: "text-[14px] font-medium"
              }}
            />
            <p className="text-[12px] text-[#71717A] mt-2">
              {t('admin.creators.actions.topUpBalance.hint')}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="light"
              onPress={() => setBalanceModal(false)}
              isDisabled={isProcessing}
            >
              {t('global.cancel')}
            </Button>
            <Button
              color="primary"
              onPress={handleTopUpBalance}
              isLoading={isProcessing}
            >
              {t('admin.creators.actions.topUpBalance.confirm')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default CreatorActionsCell;
